import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { XlsxConversionError } from '@/lib/custom-dashboard/csv-to-xlsx';
import {
  attachDashboardDownloadReceiver,
  type DashboardDownloadRequest,
  type DashboardPdfDownloadRequest,
  formatDownloadSize,
  isPdfDownloadRequest,
  saveDashboardDownload,
  saveDashboardPdf,
} from '@/lib/custom-dashboard/dashboard-download';
import { PdfConversionError } from '@/lib/custom-dashboard/images-to-pdf';
import {
  createSandboxedIframe,
  watchIframeNavigation,
} from '@/lib/custom-dashboard/srcdoc-wrapper';
import {
  attachDashboardDataSender,
  buildInjectionSrcdoc,
  createNonce,
} from '@/lib/custom-dashboard/srcdoc-injection';

export interface CustomDashboardViewerProps {
  html: string;
  dataBuffer: ArrayBuffer;
  onNavigation: () => void;
  /**
   * 閲覧者がこの Viewer を見ているか。keep-alive で裏に残るタブは false を渡す。
   * false の間はダウンロード要求を破棄し、保留中の確認ダイアログも閉じる
   * （裏タブの成果物が前面のタブの上に確認を出せないようにする）。
   */
  isActive?: boolean;
  /** 確認ダイアログに表示する要求元の名前（タブ名など）。 */
  sourceName?: string;
}

// 変換要求のサイズは保存されるファイルではなく受け取った材料（CSV・ページ画像）のものなので、そう読めるようにする。
const describeDownloadSize = (request: DashboardDownloadRequest): string => {
  if (isPdfDownloadRequest(request)) {
    const imageBytes = request.pages.reduce((total, page) => total + page.byteLength, 0);
    return `PDF ${request.pages.length} ページ・画像 ${formatDownloadSize(imageBytes)} から作成`;
  }
  const size = formatDownloadSize(request.bytes.byteLength);
  return request.convertFromCsv ? `CSV ${size} から変換` : size;
};

const confirmPdfDownload = async (request: DashboardPdfDownloadRequest): Promise<void> => {
  try {
    await saveDashboardPdf(request);
  } catch (error) {
    if (!(error instanceof PdfConversionError)) {
      toast.error('PDF を作成できませんでした');
      throw error;
    }
    toast.error(`PDF を作成できませんでした: ${error.message}`);
  }
};

const confirmDownload = (request: DashboardDownloadRequest): void => {
  if (isPdfDownloadRequest(request)) {
    void confirmPdfDownload(request);
    return;
  }
  try {
    saveDashboardDownload(request);
  } catch (error) {
    if (!(error instanceof XlsxConversionError)) throw error;
    toast.error(`Excel 形式に変換できませんでした: ${error.message}`);
  }
};

export function CustomDashboardViewer({
  html,
  dataBuffer,
  onNavigation,
  isActive = true,
  sourceName,
}: CustomDashboardViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  // 保留中のダウンロード要求は 1 件だけ持ち、新しい要求で置き換える（連打による確認の山積みを防ぐ）。
  const [pendingDownload, setPendingDownload] = useState<DashboardDownloadRequest | null>(null);
  // isActive の変化で iframe を作り直さない（keep-alive を壊さない）よう ref 経由で参照する。
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;

  useEffect(() => {
    if (!isActive) setPendingDownload(null);
  }, [isActive]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const nonce = createNonce();
    const iframe = createSandboxedIframe(buildInjectionSrcdoc(html, nonce));
    iframe.title = 'カスタムダッシュボード';
    iframe.className = 'h-full w-full border-0 bg-white';
    const stopWatching = watchIframeNavigation(iframe, onNavigation);
    // transferで元のQueryキャッシュをdetachしないよう、送信用の所有権をコピーに分離する。
    const stopSending = attachDashboardDataSender(iframe, nonce, dataBuffer.slice(0));
    const stopReceivingDownloads = attachDashboardDownloadReceiver(iframe, nonce, (request) => {
      if (!isActiveRef.current) {
        console.warn('表示中でないダッシュボードからのダウンロード要求を破棄しました', {
          filename: request.filename,
        });
        return;
      }
      setPendingDownload(request);
    });
    container.replaceChildren(iframe);

    return () => {
      stopReceivingDownloads();
      stopSending();
      stopWatching();
      iframe.remove();
      setPendingDownload(null);
    };
  }, [dataBuffer, html, onNavigation]);

  return (
    <>
      <div ref={containerRef} className="min-h-0 flex-1" />
      <AlertDialog
        open={pendingDownload !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDownload(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>ファイルをダウンロードしますか？</AlertDialogTitle>
            <AlertDialogDescription>
              {sourceName ? `ダッシュボード「${sourceName}」` : 'ダッシュボード'}が「
              {pendingDownload?.filename}」（
              {pendingDownload && describeDownloadSize(pendingDownload)}
              ）の保存を要求しています。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingDownload) confirmDownload(pendingDownload);
              }}
            >
              ダウンロード
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
